import React, { useState, useEffect, useMemo } from "react";
import axios from "axios";

import { Table, Container, Button } from "react-bootstrap";
import { useNavigate } from "react-router-dom";

import { toast } from "react-toastify";
import { ADMIN_TUTOR_API } from "../../../api/api";

import { FaEye, FaSort, FaSortUp, FaSortDown } from "react-icons/fa";
import { RiArrowDownSLine } from "react-icons/ri";

import { exportToExcel } from "../../../utils/exportExcel";
import { handleApiError } from "../../../utils/apiErrorHandler";
import { useSortableTable } from "../../../hooks/useSortableTable";

import excelImg from "../../../assets/excel-icon.png";
import Nofound from "../../../assets/not-found.png";

import "./AdminAllTutorManagement.css";

const AdminAllTutorManagement = () => {
  const navigate = useNavigate();
  const [tutors, setTutors] = useState([]);

  // Filter states
  const [selectedTutorFilter, setSelectedTutorFilter] = useState("All Tutors");
  const [selectedStatusFilter, setSelectedStatusFilter] =
    useState("All Status");

  //loading
  const [loadingAllTutors, setLoadingAllTutors] = useState(true);

  // main toast error
  const showErrorToast = (message) => {
    toast.error(message, {
      position: "top-right",
      autoClose: 1500,
      theme: "colored",
    });
  };

  useEffect(() => {
    const token = localStorage.getItem("token");

    if (!token) {
      setLoadingAllTutors(false);
      navigate("/admin-signin");
      return;
    }

    const fetchTutors = async () => {
      try {
        const response = await axios.get(ADMIN_TUTOR_API.GET_ALL_TUTORS, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });

        const data = response.data;

        if (data.statusCode === 200) {
          setTutors(data.data || []);
        } else {
          // This will handle any 2xx response that is not 201
          showErrorToast(data.message);
        }
      } catch (error) {
        console.error("Error fetching tutors:", error);
        // Server responded with non-2xx status (like 400, 500)
        
        // CHECK (expiry / invalid token)
        handleApiError(error, navigate, showErrorToast, "/admin-signin");
      } finally {
        setLoadingAllTutors(false);
      }
    };

    fetchTutors();
  }, [navigate]);

  const handleEyeClick = (tutor) => {
    const { status, tutor_id } = tutor;

    switch (status?.toLowerCase()) {
      case "published":
        navigate(`/header/admin/tutor-published/${tutor_id}`);
        break;
      case "pending":
        navigate(`/header/admin/tutor-pending/${tutor_id}`);
        break;
      case "rejected":
        navigate(`/header/admin/tutor-rejected/${tutor_id}`);
        break;
      default:
        break;
    }
  };

  // Dynamic dropdown options
  const filteredTutorsForDropdown = useMemo(
    () => ["All Tutors", ...new Set(tutors.map((t) => t.full_name?.trim()))],
    [tutors],
  );

  const filteredStatusForDropdown = useMemo(() => {
    let filtered = tutors;

    if (selectedTutorFilter !== "All Tutors") {
      filtered = tutors.filter(
        (t) => t.full_name?.trim() === selectedTutorFilter.trim(),
      );
    }

    return [
      "All Status",
      ...new Set(filtered.map((t) => t.status?.trim()).filter(Boolean)),
    ];
  }, [tutors, selectedTutorFilter]);

  // // Filtered name based on selection explicit return
  const filteredTutors = useMemo(() => {
    return tutors.filter((tutor) => {
      const name = tutor.full_name?.trim() || "";
      const status = tutor.status?.toLowerCase() || "";

      const matchesName =
        selectedTutorFilter === "All Tutors" || name === selectedTutorFilter;

      const matchesStatus =
        selectedStatusFilter === "All Status" ||
        status === selectedStatusFilter.toLowerCase();

      return matchesName && matchesStatus;
    });
  }, [tutors, selectedTutorFilter, selectedStatusFilter]);

  // SORT HOOK (MAIN ADDITION)
  const { sortedData, sortConfig, handleSort } = useSortableTable(
    filteredTutors,
    "full_name",
  );

  const handleExportTutors = () => {
    // Format data for Excel
    const formattedData = sortedData.map((tutor) => ({
      Name: tutor.full_name || "N/A",
      Experience: tutor.years_of_experience || "N/A",
      Education: tutor.degree || "N/A",
      Email: tutor.email || "N/A",
      Level: tutor.level || "N/A",
      Phone: tutor.phone_number || "N/A",
      Status: tutor.status || "N/A",
    }));

    exportToExcel({
      data: formattedData,
      fileName: "Tutors",
      sheetName: "Tutors",
    });
  };

  //  SORT ICON HELPER
  const renderSortIcon = (key) => {
    if (sortConfig.key !== key) return <FaSort size={11} />;
    if (sortConfig.direction === "asc") return <FaSortUp size={11} />;
    return <FaSortDown size={11} />;
  };

  return (
    <Container className="admin-all-tutor-section">
      <h4 className="admin-dashboard-title">Tutor Management</h4>
      <p className="admin-dashboard-subtitle">
        Manage tutor profiles, review their information, and monitor their account status.
      </p>

      <div className="admin-all-tutor-table-scroll-wrapper">
        <div className="admin-all-tutor-header">
          <div className="admin-all-courses-header-left">
            <h5 className="admin-all-tutor-header-title">
              All Tutors{" "}
              <span className="admin-all-tutor-count">
                ({sortedData.length})
              </span>
            </h5>
          </div>

          <div className="admin-all-courses-header-right">
            <div className="admin-course-filter-wrapper">
              <select
                className="admin-course-filter-select"
                value={selectedTutorFilter}
                onChange={(e) => setSelectedTutorFilter(e.target.value)}
              >
                {filteredTutorsForDropdown.map((name, i) => (
                  <option key={i} value={name}>
                    {name}
                  </option>
                ))}
              </select>
              <RiArrowDownSLine className="admin-status-filter-select-arrow" />
            </div>

            <div className="admin-status-filter-wrapper">
              <select
                className="admin-course-filter-select"
                value={selectedStatusFilter}
                onChange={(e) => setSelectedStatusFilter(e.target.value)}
              >
                {filteredStatusForDropdown.map((status, i) => (
                  <option key={i} value={status}>
                    {status}
                  </option>
                ))}
              </select>
              <RiArrowDownSLine className="admin-status-filter-select-arrow" />
            </div>

            <Button
              size="sm"
              className="admin-dashboard-table-export"
              onClick={handleExportTutors}
              disabled={sortedData.length === 0}
              title={
                sortedData.length === 0
                  ? "No data available to export"
                  : "Export to Excel"
              }
              style={{
                cursor: sortedData.length === 0 ? "not-allowed" : "pointer",
                opacity: sortedData.length === 0 ? 0.5 : 1,
              }}
            >
              <img
                src={excelImg}
                alt="Excel"
                className="admin-dashboard-table-export-excel-img"
              />
            </Button>
          </div>
        </div>

        <Table className="admin-all-tutor-table">
          <thead>
            <tr>
              <th
                onClick={() => handleSort("full_name")}
                style={{ cursor: "pointer" }}
              >
                Tutors {renderSortIcon("full_name")}
              </th>

              <th
                onClick={() => handleSort("years_of_experience")}
                style={{ cursor: "pointer" }}
              >
                Experience {renderSortIcon("years_of_experience")}
              </th>

              <th
                onClick={() => handleSort("degree")}
                style={{ cursor: "pointer" }}
              >
                Education {renderSortIcon("degree")}
              </th>

              <th
                onClick={() => handleSort("email")}
                style={{ cursor: "pointer" }}
              >
                Email {renderSortIcon("email")}
              </th>
              <th
                onClick={() => handleSort("level")}
                style={{ cursor: "pointer" }}
              >
                Level {renderSortIcon("level")}
              </th>
              <th>Phone</th>
              <th
                onClick={() => handleSort("status")}
                style={{ cursor: "pointer" }}
              >
                Status {renderSortIcon("status")}
              </th>
              <th>Action</th>
            </tr>
          </thead>

          <tbody>
            {loadingAllTutors ? (
              <tr>
                <td colSpan="10" style={{ textAlign: "center" }}>
                  Loading tutors...
                </td>
              </tr>
            ) : sortedData.length > 0 ? (
              sortedData.map((tutor) => (
                <tr key={tutor.tutor_id}>
                  <td
                    title={tutor.full_name}
                    className="admin-all-tutor-table-td"
                  >
                    {tutor.full_name || "N/A"}
                  </td>
                  <td>{tutor.years_of_experience || "N/A"}</td>
                  <td title={tutor.degree} className="admin-all-tutor-table-td">
                    {tutor.degree || "N/A"}
                  </td>
                  <td title={tutor.email} className="admin-all-tutor-table-td">
                    {tutor.email || "N/A"}
                  </td>
                  <td>
                    <span
                      className={`admin-all-tutor-level ${tutor.level?.toLowerCase()}`}
                    >
                      {tutor.level || "N/A"}
                    </span>
                  </td>
                  <td>{tutor.phone_number || "N/A"}</td>
                  <td>
                    <span
                      className={`admin-all-tutor-status ${tutor.status?.toLowerCase()}`}
                    >
                      {tutor.status || "N/A"}
                    </span>
                  </td>
                  <td className="admin-all-tutor-section-eye">
                    <FaEye
                      onClick={() => handleEyeClick(tutor)}
                      style={{
                        cursor: tutor.status ? "pointer" : "not-allowed",
                        opacity: tutor.status ? 1 : 0.5,
                      }}
                      title={
                        tutor.status
                          ? "View tutor details"
                          : "No tutor details available"
                      }
                    />
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan="10" className="admin-all-tutor-empty-table">
                  <img
                    src={Nofound}
                    alt="No Data Found"
                    className="admin-all-tutor-empty-img"
                  />
                  <p>No Data Found</p>
                </td>
              </tr>
            )}
          </tbody>
        </Table>
      </div>
    </Container>
  );
};

export default AdminAllTutorManagement;
